import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Code, Monitor, Shield, Users } from 'lucide-react';

const AboutSection = ({ companyInfo }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const features = [
    {
      icon: Code,
      title: 'Software & ERP',
      description: 'Custom enterprise solutions'
    },
    {
      icon: Monitor,
      title: 'AI & Data Analytics',
      description: 'Intelligent data insights'
    },
    {
      icon: Shield,
      title: 'Cybersecurity',
      description: 'Comprehensive protection'
    },
    {
      icon: Users,
      title: 'Global Partnerships',
      description: 'Worldwide collaboration'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: 'easeOut' }
    }
  };

  return (
    <section id="about" className="py-20 md:py-24 bg-lion-gray">
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-12 items-center" ref={ref}>
          {/* Left side - Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
          >
            <motion.span 
              variants={itemVariants}
              className="font-bold text-lion-yellow text-sm uppercase tracking-wider"
            >
              WHO WE ARE
            </motion.span>
            
            <motion.h2 
              variants={itemVariants}
              className="text-3xl md:text-4xl font-poppins font-bold text-lion-blue mt-2 mb-6"
            >
              {companyInfo?.aboutTitle || 'A Global Technology Leader from Africa'}
            </motion.h2>
            
            <div className="space-y-4">
              {companyInfo?.aboutDescription ? (
                (() => {
                  // Parse aboutDescription if it's a string, otherwise use as array
                  let descriptions;
                  try {
                    descriptions = typeof companyInfo.aboutDescription === 'string' 
                      ? JSON.parse(companyInfo.aboutDescription)
                      : companyInfo.aboutDescription;
                  } catch (e) {
                    // If parsing fails, treat as a single paragraph
                    descriptions = [companyInfo.aboutDescription];
                  }
                  
                  // Ensure it's an array
                  if (!Array.isArray(descriptions)) {
                    descriptions = [descriptions];
                  }
                  
                  return descriptions.map((paragraph, index) => (
                    <motion.p 
                      key={index}
                      variants={itemVariants}
                      className="text-gray-600 leading-relaxed"
                    >
                      {paragraph}
                    </motion.p>
                  ));
                })()
              ) : (
                <>
                  <motion.p 
                    variants={itemVariants}
                    className="text-gray-600 leading-relaxed"
                  >
                    LionTek is more than a technology company; we are a vision for the future. 
                    From our strategic base in Freetown, Sierra Leone, we develop world-class 
                    technology and nurture talent to compete on a global stage.
                  </motion.p>
                  
                  <motion.p 
                    variants={itemVariants}
                    className="text-gray-600 leading-relaxed"
                  >
                    Our objective is to leverage cutting-edge technology—including AI, data analytics, 
                    and cybersecurity—to build a sustainable digital ecosystem that drives innovation 
                    and growth for our partners worldwide.
                  </motion.p>
                </>
              )}
            </div>
          </motion.div>

          {/* Right side - Feature Cards */}
          <motion.div 
            className="grid grid-cols-2 gap-6"
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
          >
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  variants={cardVariants}
                  custom={index}
                  className="bg-white p-6 rounded-lg text-center transform hover:-translate-y-2 transition-all duration-300 shadow-md hover:shadow-xl group cursor-pointer"
                >
                  <div className="flex justify-center mb-4">
                    <Icon className="h-12 w-12 text-lion-blue group-hover:text-lion-yellow transition-colors duration-300" />
                  </div>
                  <h3 className="font-poppins font-semibold text-lion-blue mb-2 group-hover:text-lion-yellow transition-colors duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {feature.description}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;